import { CollectionFilter } from "@/modules/DashboardTemplate/models/CollectionFilter";
import { OptionsChart } from "@/modules/DashboardTemplate/models/optionsChart";
import { PropertiesChart } from "@/modules/DashboardTemplate/models/PropertiesChart";
import { SectionOfDashboard } from "@/modules/DashboardTemplate/models/SectionOfDashboard";
import { Utils } from "@/shared/utils/utils";
import { Component, Inject, Prop, Vue, Watch } from "vue-property-decorator";
import { mapGetters } from "vuex";
import { LEGEND_CHART, TITLE_CHART } from "../ChartsConstants";
import { DynamicSelectData } from "@/shared/components/DynamicSelect/model/DynamicSelectData";
import { UserService } from "@/modules/User/services/UserService";
import { getUserByDocument } from "@/modules/DashboardTemplate/Utils";

@Component({
  name: "StackedPolarChart",
  computed: {
    ...mapGetters({
      sectionsOfDashboardStore: "getSectionsOfdashboard",
      refreshCharts: "getRefreshCharts"
    })
  }
})
export default class StackedPolarChart extends Vue {
  sectionsOfDashboardStore!: Array<SectionOfDashboard>;
  refreshCharts!: number;

  @Inject() userService!: UserService;

  @Prop() propertiesToChart!: PropertiesChart;
  @Prop() dataToGraph?: CollectionFilter;

  showMenu = false;
  menuX = 0;
  menuY = 0;
  selectedBarData: any = null;
  tooltipParams: any = null; // Guardar los parámetros del tooltip
  chartOptions: OptionsChart = new OptionsChart();

  mounted() {
    this.chart();

    this.$nextTick(() => {
      const chartRef = this.$refs.myChart as any;
      if (chartRef && chartRef.$el) {
        chartRef.$el.addEventListener("contextmenu", (e: MouseEvent) => {
          e.preventDefault();
          this.handleRightClick(e);
        });
      }
    });
  }

  beforeDestroy() {
    const chartRef = this.$refs.myChart as any;
    if (chartRef && chartRef.$el) {
      chartRef.$el.removeEventListener("contextmenu", this.handleRightClick);
    }
  }

  @Watch("refreshCharts")
  @Watch("propertiesToChart", { deep: true })
  onChangeData() {
    this.chart();
  }

  chart() {
    if (!this.dataToGraph) return;
    const dataToGraph = this.dataToGraph;
    const chartData = dataToGraph.chartData;

    const series = chartData.data.map((item: any) => ({
      name: item.name == "" ? "Sin Info" : item.name,
      type: "bar",
      stack: item.stack,
      label:
        item.labelBar != ""
          ? {
              show: true,
              formatter: item.stack,
              position: "outside",
              color: "#000",
              fontSize: 45
            }
          : { show: false },
      data: item.data,
      coordinateSystem: "polar"
    }));
    const groupForLegend = this.getGroupLegend(chartData.groups);
    const color = Utils.generateColorsTones(
      this.propertiesToChart.chartColor.hex,
      6
    );
    this.chartOptions = {
      color,
      tooltip: { trigger: "item" },
      title: {
        ...TITLE_CHART,
        text: dataToGraph.chartName
      },
      legend: {
        ...LEGEND_CHART,
        data: groupForLegend
      },
      series,
      angleAxis: {},
      radiusAxis: {
        type: "category",
        data: chartData.xAxis,
        z: 10
      },
      polar: {
        radius: "54%",
        center: ["35%", "50%"]
      }
    };
  }

  handleClick(event: any) {
    this.$emit("click", {
      chartType: "pie",
      event
    });
  }

  handleRightClick(e: MouseEvent) {
    e.preventDefault();

    // Validación: solo funciona para groupBy = "document" o "owner"
    const allowedGroupBy = ["document", "owner", "user", "userId"];
    const groupBy = (this.propertiesToChart
      .groupBy as DynamicSelectData[])?.find((group: any) =>
      allowedGroupBy.includes(group.id)
    );

    if (!groupBy) {
      return;
    }

    // Obtener los datos del gráfico en el momento del click
    const chartRef = this.$refs.myChart as any;
    if (!chartRef || !chartRef.chart) return;

    const chart = chartRef.chart;

    // Obtener la posición del mouse relativa al canvas
    const canvas = chart.getDom().querySelector("canvas");
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Detectar si el click fue sobre una barra
    const params = chart.getZr().handler.findHover(x, y);

    if (params && params.target && params.target.dataIndex !== undefined) {
      // Construir el objeto con los datos del elemento clickeado
      const dataIndex = params.target.dataIndex;
      const seriesIndex = params.target.seriesIndex || 0;
      const series = chart.getOption().series[seriesIndex];

      this.tooltipParams = {
        seriesName: series.name,
        name: this.dataToGraph?.chartData.xAxis[dataIndex],
        value: series.data[dataIndex],
        dataIndex: dataIndex,
        seriesIndex: seriesIndex,
        data: series.data[dataIndex]
      };

      this.selectedBarData = this.tooltipParams;

      // Ocultar el tooltip
      chart.dispatchAction({ type: "hideTip" });

      // Mostrar el menú contextual
      this.menuX = e.clientX;
      this.menuY = e.clientY;
      this.showMenu = true;
    }
  }
  detectDocument() {
    const allowedGroupBy = ["document", "owner", "user", "userId"];
    const groupById = (this.propertiesToChart.groupBy[0] as DynamicSelectData)
      ?.id;

    if (groupById && allowedGroupBy.includes(groupById)) {
      return this.selectedBarData ? this.selectedBarData.name : "";
    }
    if (this.selectedBarData && this.selectedBarData.seriesName) {
      if (this.selectedBarData.seriesName.split("-").length > 1) {
        const potentialNumber = this.selectedBarData.seriesName.split("-");
        if (!isNaN(Number(potentialNumber[1].trim()))) {
          return potentialNumber[1].trim();
        }
        return potentialNumber[0].trim();
      }
      return this.selectedBarData.seriesName;
    }
    return "";
  }
  async onMenuAction() {
    // Obtener la cédula del paciente seleccionado
    const userOrId = this.detectDocument();

    // Buscar usuario usando la función de utils
    const userId = await getUserByDocument(userOrId, this.userService);

    // Si se encontró el usuario, abrir su perfil en nueva pestaña
    if (userId) {
      const route = this.$router.resolve({ path: `/profile/${userId}` });
      window.open(route.href, "_blank");
      this.showMenu = false;
      return;
    }

    // Cerrar el menú contextual
    this.showMenu = false;
  }

  getGroupLegend(groups: any): string[] {
    if (!groups) return [];
    if (Array.isArray(groups)) {
      return groups.map(g => g || "Sin Info");
    }
    const keys = Object.keys(groups);
    if (keys.length === 0) return [];

    const firstKey = keys[0];
    const values = groups[firstKey] || [];

    return values.map((v: any) => v || "Sin Info");
  }
}
